import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
    CoreActionType,
    loadCustomProperties,
    loadCustomPropertiesSuccess,
    loadInstallationTypes,
    loadInstallationTypesFailure,
    loadInstallationTypesSuccess,
    loadLocations,
    loadLocationsSuccess,
    loadModemProviders,
    loadModemProvidersFailure,
    loadModemProvidersSuccess,
    loadQFinalEntities,
    loadQFinalEntitiesSuccess,
    OptionsActionType,
} from '../actions';
import { catchError, map, of, switchMap } from 'rxjs';
import { LocationService } from 'app/shared/services/location.service';
import { ModemSetupService } from 'app/shared/services/modem-setup.service';
import { EntityService } from 'app/shared/services/entity.service';
import { LocationDashboardService } from 'app/shared/services/location-dashboard.service';
import { UiUtilsService } from 'app/shared/utils/ui-utils.service';
import { TranslateService } from '@ngx-translate/core';
import { Action } from '@ngrx/store';
import { FLOW_DISPLAY_GROUP, QFINAL, QUANTITY } from 'app/shared/constant';
import { EntityResponseItem } from 'app/shared/models/view-data';

@Injectable()
export class OptionsEffects {
    private actions$: Actions<Action> = inject(Actions);
    private translateService = inject(TranslateService);

    private locationService = inject(LocationService);
    private modemSetupService = inject(ModemSetupService);
    private entityService = inject(EntityService);
    private locationDashboardService = inject(LocationDashboardService);

    private uiUtilsService = inject(UiUtilsService);

    constructor() {}

    loadInstallationTypes$ = createEffect(() =>
        this.actions$.pipe(
            ofType(loadInstallationTypes),
            switchMap(({ installationId }) =>
                this.locationService.getInstallationType(installationId).pipe(
                    map((installationTypes) => loadInstallationTypesSuccess({ data: installationTypes })),
                    catchError((error) => of(loadInstallationTypesFailure({ error }))),
                ),
            ),
        ),
    );

    loadModemProviders$ = createEffect(() =>
        this.actions$.pipe(
            ofType(loadModemProviders),
            switchMap(() =>
                this.modemSetupService.getAll().pipe(
                    map((modemProviders) =>
                        loadModemProvidersSuccess({
                            data: modemProviders.map((item, index) => ({
                                id: index,
                                name: item.provider,
                                isChecked: false,
                            })),
                        }),
                    ),
                    catchError(() =>
                        of(
                            loadModemProvidersFailure({
                                optionType: this.translateService.instant('COMMON.MODEM_PROVIDER_FETCH_ERROR'),
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    loadQFinalEntities$ = createEffect(() =>
        this.actions$.pipe(
            ofType(loadQFinalEntities),
            switchMap(({ customerId, locationId }) =>
                this.entityService.getEntitiesByLocation(customerId, [locationId]).pipe(
                    map((entities: Array<EntityResponseItem>) =>
                        entities.filter(
                            (entity) =>
                                entity.displayGroup === FLOW_DISPLAY_GROUP &&
                                entity.entityName.toLowerCase() !== QFINAL.toLowerCase() &&
                                entity.entityName.toLowerCase() !== QUANTITY.toLowerCase(),
                        ),
                    ),
                    map((entities) => loadQFinalEntitiesSuccess({ data: entities })),
                    catchError((error) => of(loadInstallationTypesFailure({ error }))),
                ),
            ),
        ),
    );

    loadLocations$ = createEffect(() =>
        this.actions$.pipe(
            ofType(loadLocations),
            switchMap(({ customerId }) =>
                this.locationDashboardService.getLocationsList({ customerId, IncludeInactiveLocations: true }).pipe(
                    map((locations) =>
                        loadLocationsSuccess({
                            locations: locations ? locations.sort(this.uiUtilsService.sortLocations) : [],
                        }),
                    ),
                ),
            ),
        ),
    );

    loadCustomProps$ = createEffect(() =>
        this.actions$.pipe(
            ofType(loadCustomProperties),
            switchMap(({ customerId }) =>
                this.locationService.getCustomPropNames(customerId).pipe(
                    map((customProperties) => loadCustomPropertiesSuccess({ data: customProperties })),
                    catchError((error) => of(loadCustomPropertiesSuccess({ data: [] }))),
                ),
            ),
        ),
    );
}
